/**
 * The account's cards' and sheets' shared parts (part 36a; from part 36c shared with the account page's sheets and the
 * mailed links' cards; PLAN.md §3, step 7: built from the library): the gate's frame and a card's heading, Back, a field's
 * error, the email field, the submit that says it is working, a new password's strength, and a problem or a wait in words.
 */
import { createSignal, onMount } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { Problem } from '../account/flows.ts';
import { acceptable, loadScorer, strengthOf } from '../account/strength.ts';
import type { Scorer, Strength } from '../account/strength.ts';
import Button from '../ui/Button.ts';
import Card from '../ui/Card.ts';
import Field from '../ui/Field.ts';
import TextField from '../ui/TextField.ts';
import { component, el } from '../ui/solid.ts';
import type { Child } from '../ui/solid.ts';
import type { Shell } from './shell.ts';

/** The seconds of a wait in words: "a minute" or less as seconds, else whole minutes rounded up. */
export function waitWords(shell: Shell, seconds: number): string {
  return seconds < 60 ? shell.t('account.seconds', { count: Math.max(1, seconds) }) : shell.t('account.minutes', { count: Math.ceil(seconds / 60) });
}

/** What a problem tells the writer (LG6–LG12, SU4, SU5, SU7). */
export function problemWords(shell: Shell, problem: Problem): string {
  const { t } = shell;
  if (problem.kind === 'unsafe') return t('account.error.unsafe');
  if (problem.kind === 'failed') return t('account.error.failed');
  switch (problem.error) {
    case 'login-failed':
      return t('account.error.wrongLogin');
    case 'login-throttled':
    case 'rate-limited':
      return problem.retryAfter === null ? t('account.error.tooMany') : t('account.error.wait', { wait: waitWords(shell, problem.retryAfter) });
    case 'too-many-devices':
      return t('account.error.devices');
    case 'second-factor-failed':
      return t('account.error.code');
    case 'ticket-invalid':
      return t('account.error.ticket');
    case 'terms-outdated':
      return t('account.error.outdated');
    case 'invalid-link':
      return t('account.error.link');
    case 'passkey-unavailable':
      return t('account.error.passkeyOff');
    // Words the flows here can't meet, or a relay failing (`internal`, `timeout`): the network's failure to the writer.
    // Part 38's share-link words among them: no account flow sends a share-link request (part 38d words its own); and
    // part 38a's invite words: no flow sends an invite request yet, and no account holds a keyring before part 38c.
    case 'not-found':
    case 'timeout':
    case 'unauthenticated':
    case 'bad-length':
    case 'too-large':
    case 'internal':
    case 'bad-request':
    case 'bad-challenge':
    case 'device-revoked':
    case 'device-expired':
    case 'second-factor-on':
    case 'grant-invalid':
    case 'login-required':
    case 'logged-out':
    case 'second-factor-off':
    case 'merchant-unavailable':
    case 'account-deleted':
    case 'ops-unavailable':
    case 'link-ended':
    case 'link-unavailable':
    case 'purchase-required':
    case 'link-exists':
    case 'too-many-links':
    case 'over-quota':
    case 'link-full':
    case 'comments-off':
    case 'item-exists':
    case 'link-gone':
    case 'share-gone':
    case 'invite-gone':
    case 'invite-ended':
    case 'invite-claimed':
    case 'invite-address':
    case 'share-ended':
    case 'share-full':
    case 'copy-stale':
    case 'keyring-required':
    case 'change-pending':
    case 'keyring-exists':
    case 'share-exists':
    case 'invite-exists':
    case 'notes-off':
    case 'copy-unavailable':
      return t('account.error.failed');
  }
}

/** The gate's frame: the wordmark over one card (the library's Card), the page's main landmark. */
export function Frame(shell: Shell, card: Child): Child {
  return el(
    'div',
    { class: 'ac-gate' },
    el('header', { class: 'ac-gate__top' }, el('p', { class: 'wordmark' }, shell.t('app.name'))),
    el('main', { class: 'ac-gate__main' }, component(Card, { class: 'ac-gate__card', children: card })),
  );
}

/** A card's heading, which takes the focus as the card opens (the part log's accessibility list). */
export function Heading(id: string, text: string, className: string): Child {
  return el('h1', {
    id,
    class: className,
    tabIndex: -1,
    ref: (heading: HTMLElement) => {
      onMount(() => {
        heading.focus();
      });
    },
  }, text);
}

/** The card's "Back", to `step` (the library's ghost Button with its back mark). */
export function Back(shell: Shell, step: () => void): Child {
  return component(Button, { variant: 'ghost', size: 'sm', iconStart: 'back', class: 'ac-back', onClick: step, children: shell.t('account.back') });
}

/** A field's error, tied to it by `aria-describedby` and said once (a polite live region of its own). */
export function ErrorText(id: string, text: Accessor<string>): Child {
  return el('p', { id, class: 'ac-error', role: 'status' }, text);
}

/**
 * An email field (the library's Field and TextField), labelled `label` (the account's email unless given): required, as
 * the input always was, with no "Required" mark, since a card asks only for what it needs.
 */
export function EmailField(shell: Shell, id: string, autocomplete: string, value: Accessor<string>, set: (value: string) => void, describedBy: string, label: string = shell.t('account.email')): Child {
  return component(Field, {
    id,
    label,
    required: true,
    hideMarker: true,
    get children() {
      return component(TextField, {
        type: 'email',
        autocomplete,
        inputMode: 'email',
        'aria-describedby': describedBy,
        get value() {
          return value();
        },
        onChange: (typed: string) => {
          set(typed);
        },
      });
    },
  });
}

/** How a {@link Submit} looks: the card's full-width large primary unless said. */
export interface SubmitLook {
  /** Off (`aria-disabled`) yet pressable, so the form says what is missing. */
  readonly off?: () => boolean;
  /** The hook class. */
  readonly class?: string;
  /** Danger for an action that destroys (Delete account). */
  readonly variant?: 'primary' | 'danger';
  /** The card's full-width large button (default), or a row's medium one. */
  readonly full?: boolean;
  /** What it says while working (part 41b: the backup password's own words). */
  readonly working?: string;
}

/**
 * A submit button (the library's) that says it is working (Argon2id takes about a second on a phone) and keeps its place
 * meanwhile; off (`aria-disabled`) while busy or off, yet pressable.
 */
export function Submit(shell: Shell, label: string, busy: Accessor<boolean>, look: SubmitLook = {}): Child {
  const full = look.full ?? true;
  const working = look.working ?? shell.t('account.working');
  const off = look.off ?? (() => false);
  return component(Button, {
    type: 'submit',
    variant: look.variant ?? 'primary',
    size: full ? 'lg' : 'md',
    fullWidth: full,
    class: look.class,
    get 'aria-disabled'() {
      return busy() || off() ? 'true' : 'false';
    },
    get 'aria-busy'() {
      return busy() ? 'true' : 'false';
    },
    get children() {
      return busy() ? working : label;
    },
  });
}

/** Focuses `#id`'s element (the first wrong field after a failed submit). */
export function focusField(id: string): void {
  document.getElementById(id)?.focus();
}

/** A new password's strength as it is typed (decision 25, SU3): what was typed, its strength, and the check before a submit. */
export interface NewStrength {
  /** Takes what was typed, and starts loading the scorer. */
  readonly set: (password: string) => void;
  /** The strength of `password` (what was typed, unless given) against `email`, once the scorer is in. */
  readonly strength: (password?: string) => Strength;
  /** The words of what is wrong, or null for a password that will do. */
  readonly check: () => Promise<string | null>;
}

/** The strength of a new password (the field's look is the caller's: the sheets' and the cards' library field, `SheetNewPassword`). */
export function newStrength(shell: Shell, email: Accessor<string>): NewStrength {
  const { t } = shell;
  const [password, setPassword] = createSignal('');
  const [scorer, setScorer] = createSignal<Scorer | null>(null);
  let loading: Promise<void> | null = null;
  const load = (): Promise<void> =>
    (loading ??= loadScorer().then(
      (loaded) => {
        setScorer(() => loaded);
      },
      () => {
        loading = null;
      },
    ));
  const strength = (typed: string = password()): Strength => strengthOf(typed, scorer(), email());
  // SU3: never weak for want of the scorer: the check waits for it, and a scorer that can't load is the connection's
  // problem, said as such.
  const check = async (): Promise<string | null> => {
    if (scorer() === null) await load();
    if (scorer() === null) return problemWords(shell, { kind: 'failed' });
    if (password() === '') return t('account.error.password');
    return acceptable(strength()) ? null : t('account.error.weak');
  };
  return {
    set: (value) => {
      setPassword(value);
      void load();
    },
    strength,
    check,
  };
}
